import React from 'react';
import BookingFilter from './BookingFilter';
import BookingGrid from './BookingGrid';
import EquipmentBookingGrid from './EquipmentBookingGrid';

/**
 * Memoized BookingFilter component
 * Prevents re-rendering when props haven't changed
 */
export const MemoizedBookingFilter = React.memo(BookingFilter, (prevProps, nextProps) => {
    const propsToCompare = [
        'resetAll',
        'showAdvanceFilter',
        'disableResetAll',
        'searchQuery',
        'view',
        'isEquipmentView',
        'savedFilter',
        'showFilters'
    ];

    for (const prop of propsToCompare) {
        if (prevProps[prop] !== nextProps[prop]) {
            return false;
        }
    }

    if (JSON.stringify(prevProps.selectedDateRange) !== JSON.stringify(nextProps.selectedDateRange)) {
        return false;
    }

    if (prevProps.signeeFilter?.userId !== nextProps.signeeFilter?.userId) {
        return false;
    }

    return true;
});

/**
 * Memoized BookingGrid component
 * Prevents re-rendering when props haven't changed
 */
export const MemoizedBookingGrid = React.memo(BookingGrid, (prevProps, nextProps) => {
    const propsToCompare = [
        'viewData',
        'eventID',
        'isEquipmentView',
        'savedFilter',
        'showFilters'
    ];

    for (const prop of propsToCompare) {
        if (prevProps[prop] !== nextProps[prop]) {
            return false;
        }
    }

    if (prevProps.signeeFilter?.userId !== nextProps.signeeFilter?.userId) {
        return false;
    }

    return true;
});

/**
 * Memoized EquipmentBookingGrid component
 * Prevents re-rendering when props haven't changed
 */
export const MemoizedEquipmentBookingGrid = React.memo(EquipmentBookingGrid, (prevProps, nextProps) => {
    const propsToCompare = [
        'viewData',
        'eventID',
        'isEquipmentView',
        'savedFilter',
        'isTimelineView',
        'listGroupByView',
        'myBookingsMode',
        'isGroupView',
    ];

    for (const prop of propsToCompare) {
        if (prevProps[prop] !== nextProps[prop]) {
            return false;
        }
    }

    if (prevProps.signeeFilter?.userId !== nextProps.signeeFilter?.userId) {
        return false;
    }

    if (JSON.stringify(prevProps.selectedDateRange) !== JSON.stringify(nextProps.selectedDateRange)) {
        return false;
    }

    return true;
});

/**
 * Higher-order component for automatic memoization
 * Provides automatic deep comparison for common prop patterns
 */
export const withMemoization = (Component, customComparator = null) => {
    return React.memo(Component, customComparator || ((prevProps, nextProps) => {
        const prevKeys = Object.keys(prevProps);
        const nextKeys = Object.keys(nextProps);

        if (prevKeys.length !== nextKeys.length) {
            return false;
        }

        for (const key of prevKeys) {
            const prevValue = prevProps[key];
            const nextValue = nextProps[key];

            if (typeof prevValue === 'function' || typeof nextValue === 'function') {
                if (prevValue !== nextValue) {
                    return false;
                }
                continue;
            }

            if (typeof prevValue === 'object' && prevValue !== null) {
                if (JSON.stringify(prevValue) !== JSON.stringify(nextValue)) {
                    return false;
                }
                continue;
            }

            if (prevValue !== nextValue) {
                return false;
            }
        }

        return true;
    }));
};

/**
 * Memoization utilities for specific use cases
 */
export const MemoizationUtils = {
    // Compare arrays by length and content
    compareArrays: (prev, next) => {
        if (prev.length !== next.length) return false;
        return prev.every((item, index) => item === next[index]);
    },

    // Compare objects by specific keys
    compareByKeys: (prev, next, keys) => {
        return keys.every(key => prev[key] === next[key]);
    },

    // Deep comparison for nested objects
    deepCompare: (prev, next) => {
        return JSON.stringify(prev) === JSON.stringify(next);
    },

    // Compare user objects specifically
    compareUsers: (prevUser, nextUser) => {
        if (!prevUser && !nextUser) return true;
        if (!prevUser || !nextUser) return false;
        return prevUser.userId === nextUser.userId &&
            prevUser.email === nextUser.email;
    }
};

const MemoizedComponents = {
    MemoizedBookingFilter,
    MemoizedBookingGrid,
    MemoizedEquipmentBookingGrid,
    withMemoization,
    MemoizationUtils,
};

export default MemoizedComponents;